/**
 * @fileoverview Low-level Konva shape primitives and utilities.
 * 
 * **Role**: Provides reusable, low-level shape creation methods.
 * 
 * **Separation of Concerns**:
 * - `ShapeHelpers` (this file): Low-level Konva primitives (circles, rectangles, text)
 * - `ShapeFactory` (shapes.ts): High-level game objects (draggables, targets) using themes
 * 
 * **Usage Pattern**:
 * ```typescript
 * // Themes use ShapeHelpers to create basic shapes
 * const circle = ShapeHelpers.createCircle(x, y, size, color);
 * const emojiText = ShapeHelpers.createEmojiText(x, y, size, '🎨');
 * 
 * // ShapeFactory uses themes to create game objects
 * const draggables = ShapeFactory.createDraggables(pairs, layer, handlers, theme);
 * ```
 * 
 * All methods are static utilities that create configured Konva nodes.
 * No game logic, no state - just shape primitives.
 */

import Konva from 'konva';
import { CONFIG } from '../core/GameConfig.js';
import type { ColorConfig, KonvaNode } from './abstract/BaseTheme.js';

/**
 * Static utility class for creating low-level Konva shape primitives.
 * These are building blocks used by theme classes to construct game objects.
 */
export class ShapeHelpers {

    /**
     * Create a basic colored circle.
     * @param x - X position (center will be adjusted)
     * @param y - Y position (center will be adjusted)
     * @param size - Diameter of the circle
     * @param color - Fill and stroke colors
     * @returns Konva.Circle node
     */
    static createCircle(
        x: number,
        y: number,
        size: number,
        color: ColorConfig
    ): KonvaNode {
        return new Konva.Circle({
            x: x,
            y: y,
            radius: size / 2,
            fill: color.fill,
            stroke: color.stroke,
            strokeWidth: 2
        });
    }


    /**
     * Create text with a colored circle border.
     * Used by Numbers and Letters themes.
     */
    static createTextWithCircleBorder(
        x: number,
        y: number,
        size: number,
        color: ColorConfig,
        text: string
    ): KonvaNode {
        const group = new Konva.Group({ x, y });
        
        // Circle border (no fill)
        const circle = new Konva.Circle({
            x: 0,
            y: 0,
            radius: size / 2,
            fill: '',
            stroke: color.stroke,
            strokeWidth: 3
        });
        
        // Black text
        const textNode = new Konva.Text({
            x: 0,
            y: 0,
            text: text,
            fontSize: size * 0.6,
            fill: '#000000',
            fontStyle: 'bold',
            fontFamily: 'Arial, sans-serif',
            align: 'center',
            verticalAlign: 'middle'
        });

        textNode.offsetX(textNode.width() / 2);
        textNode.offsetY(textNode.height() / 2);
        circle.offsetX();
        circle.offsetY();
        
        group.add(circle);
        group.add(textNode);
        
        return group;
    }
    
    /**
     * Create emoji text shape.
     * Used by emoji themes (Animals, Fruits, Vehicles, Puzzle, Faces).
     */
    static createEmojiText(
        x: number,
        y: number,
        size: number,
        emoji: string
    ): KonvaNode {
        const text = new Konva.Text({
            x: x,
            y: y,
            text: emoji,
            fontSize: size * 1.1,
            align: 'center',
            verticalAlign: 'middle'
        });
        text.offsetX(text.width() / 2);
        text.offsetY(text.height() / 2);
        return text;
    }
    
    /**
     * Create geometric shape (circle, star, triangle, rect).
     * Used by Shapes and MovingShapes themes.
     */
    static createGeometricShape(
        x: number,
        y: number,
        size: number,
        color: ColorConfig,
        shapeType: string,
        filled: boolean = true,
        strokeWidth: number = 3
    ): KonvaNode {
        const fillColor = filled ? color.fill : '';
        
        switch (shapeType) {
            case 'circle':
                return new Konva.Circle({
                    x: x,
                    y: y,
                    radius: size / 2,
                    fill: fillColor,
                    stroke: color.stroke,
                    strokeWidth: strokeWidth
                });
            case 'star':
                return new Konva.Star({
                    x: x,
                    y: y,
                    numPoints: 5,
                    innerRadius: size / 3,
                    outerRadius: size / 2,
                    fill: fillColor,
                    stroke: color.stroke,
                    strokeWidth: strokeWidth
                });
            case 'triangle':
                const halfSize = size / 2;
                const height = (Math.sqrt(3) / 2) * size;
                return new Konva.Line({
                    x: x,
                    y: y + height / 20,
                    points: [0, height / 2, -halfSize, -height / 2, halfSize, -height / 2],
                    closed: true,
                    fill: fillColor,
                    stroke: color.stroke,
                    strokeWidth: strokeWidth
                });
            case 'rect':
            default:
                const rect = new Konva.Rect({
                    x: x,
                    y: y,
                    width: size,
                    height: size,
                    fill: fillColor,
                    stroke: color.stroke,
                    strokeWidth: strokeWidth,
                    cornerRadius: CONFIG.CORNER_RADIUS
                });
                // Center the rectangle (circles and stars are already centered)
                rect.offsetX(size / 2);
                rect.offsetY(size / 2);
                return rect;
        }
    }
}
